import { queryOptions, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { hydraUiApi } from '@/services/api';
import type { QueryConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { audienceCredentialKeys, parseAudienceCredential, type AudienceCredential } from './entity';

const STALE_TIME = 60_000;

/** @public Every ad-audience credential for the workspace (unpaginated). */
export async function getAudienceCredentials(workspaceId: number, qc: QueryClient): Promise<AudienceCredential[]> {
  const data = await hydraUiApi.audienceCredentials.list({
    params: { path: { environment_id: String(workspaceId) } },
  });
  const parsed = (data.audience_credentials ?? []).map(parseAudienceCredential);
  for (const c of parsed) {
    qc.setQueryData(audienceCredentialKeys.detail(workspaceId, c.id), c);
  }
  return parsed;
}

/** @public */
export const getAudienceCredentialsQueryOptions = (workspaceId: number, qc: QueryClient) =>
  queryOptions({
    queryKey: audienceCredentialKeys.list(workspaceId, undefined),
    queryFn: () => getAudienceCredentials(workspaceId, qc),
    staleTime: STALE_TIME,
  });

/** @public */
export const useAudienceCredentials = ({
  queryConfig,
}: { queryConfig?: QueryConfig<typeof getAudienceCredentialsQueryOptions> } = {}) => {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  return useQuery({ ...getAudienceCredentialsQueryOptions(workspaceId, qc), ...queryConfig });
};
